import isEqual from 'lodash/isEqual';
import debounce from 'lodash/debounce';
import Mustache from 'mustache';
import analytics from '../../analytics';

class FiksuGreenDiiliForm {
  /**
   * @property {HTMLElement} all class instance functionality is scoped under this one element
   */
  #element;

  /**
   * @property {CarPageApp} carPageApp instance for relating state changes accross the pag
   */
  #carPageApp;

  /**
   * @property {object} state part name
   */
  #state = {
    milege: 0,
    duration: 0,
  }; // Initial State

  /**
   * @property {string} state part name
   */
  statePartName = 'fiksugreendiiliCalculatorSettings';

  /**
   * @constructor
   * @param {HTMLElement} element element to bind to
   * @param {CarPageApp} carPageApp car page state manager
   */
  constructor(element, carPageApp) {
    this.#element = element;
    this.#carPageApp = carPageApp;
    this.debouncedMilageAnalytics = debounce(this.mileageAnalytics, 300, { 'leading': true, 'trailing': false });
    this.#findElements();
    this.#findTemplates();
    this.#bind();
    this.#initState();
  }

  /**
   * Find all elements from dom for accessing later on.
   * @private
   */
  #findElements() {
    this.elements = {
      duration: this.#element.querySelector('#fiksudiili-financing-duration'),
      distance: this.#element.querySelector('.fiksudiiliSlider'),
      price: this.#element.querySelector('.price'),
    };

    this.sliderConfigurations = JSON.parse(this.#element.querySelector('.slider-configurations').textContent);
  }

  /**
   * Mustache templates used to rerendering components
   * @private
   */
  #findTemplates() {
    this.templates = {
      sliderTemplate: this.#element.querySelector('script.slider-template').textContent,
      sliderButtonTemplate: this.#element.querySelector('script.slider-handle-template').textContent,
    };
  }

  /**
   * Resolves given duration and mileage pair into a price object
   * @param {number} rawDuration the selected duration.
   * @param {number} rawMileage the selected mileage.
   * @returns {object} price object
   * @private
   */
  #price(rawDuration, rawMileage) {
    const mileage = parseInt(rawMileage);
    const duration = parseInt(rawDuration);
    return this.fiksudiiliPrices.find(row => row.mileage === mileage && row.duration === duration) || {};
  }

  /**
   * Called when a duration radio group or mileage slider value is changed by a user.
   * Get values as parameters and update state.
   * @param {object} mileageDuration contains duration or mileage, or both.
   * @param {number} mileageDuration.duration the selected duration.
   * @param {number} mileageDuration.mileage the selected mileage.
   * @private
   */
  #handleChange(mileageDuration) {
    const { duration, mileage, price } = this.#price(
      mileageDuration?.duration || this.#state?.duration, // Update object contains one or the other
      mileageDuration?.mileage || this.#state?.mileage, // the not given value is taken from state
    );

    //Update fiksudiili duration so user made selection are reflected to fiksudiili-webstore
    sessionStorage.setItem('fiksudiiliDuration', `${duration}_${mileage}`);

    this.#carPageApp.updateState(this.statePartName, { duration, mileage, price }, true);
  }

  /**
   * Event handler for selecting a duration from the radio group
   * @param {CustomEvent} radioGroup custom event.
   */
  handleDurationChange(radioGroup) {
    const duration = parseInt(radioGroup.target.value);
    // When duration is changed, mileage is reset to it's lowest value:
    const mileage = Math.min(...this.fiksudiiliPrices.filter(fdp => fdp.duration === duration).map(fdp => fdp.mileage));
    this.#handleChange({ mileage, duration });
  }

  /**
   * Event handler for selecting a distance with the dropdown or slider (depending on view)
   * @param {CustomEvent} slider custom event.
   */
  handleDistanceChange(slider) {
    this.#handleChange({ mileage: parseInt(slider.target.getAttribute('data-current-value')) });
  }

  /**
   * Render duration slider after duration is changed, each duration have their own set of mileages.
   * @private
   */
  #renderDistance() {
    const sliderLabels = this.fiksudiiliPrices
      .filter(price => price.duration === this.#state.duration)
      .map(price => ({ key: price.mileage, label: `${price.mileage} tkm` }));

    const data = {
      ...this.sliderConfigurations, //These configurations are coming from page (rendered at Stage), one place for base configurations ;)
      labels: sliderLabels,
      labelsJSON: JSON.stringify(sliderLabels),
      initialValue: this.#state.mileage,
      steps: sliderLabels.length - 1,
    };

    const html = Mustache.render(this.templates.sliderTemplate, data, { button: this.templates.sliderButtonTemplate });
    this.elements.distance.innerHTML = html;
  }

  /**
   * Called during page load, tries to recover state from localStorage controlled by CarPageApp instance.
   * @private
   */
  #initState() {
    //Restore state form a local storage / state manager if available
    const state = this.#carPageApp.getState();
    this.fiksudiiliPrices = state.carData.fiksudiiliPrices;
    this.#onStateChanges(this.#filterState(state));
  }

  /**
   * Bind all event listeners to DOM
   * Also registers event listener for state changes.
   * @private
   */
  #bind() {
    // bind this-context to methods
    this.handleStateChanges = this.handleStateChanges.bind(this);
    this.handleDurationChange = this.handleDurationChange.bind(this);
    this.handleDistanceChange = this.handleDistanceChange.bind(this);
    // Bind methods to events
    this.elements.duration.addEventListener('change', this.handleDurationChange);
    this.elements.distance.addEventListener('slider-change', this.handleDistanceChange);
    this.#carPageApp.registerStateObserver(this.handleStateChanges);
  }

  /**
   * Filter only state related to this view
   * @private
   * @param {object} state state object
   * @returns {object|undefined} if state is available returns object otherwise undefined is returned
   */
  #filterState(state) {
    // eslint-disable-next-line no-prototype-builtins
    if (typeof state === 'object' && state.hasOwnProperty(this.statePartName)) {
      return state[this.statePartName];
    }
  }

  /**
   * Called when app state is updated
   * @private
   * @param {object} nextState updated state object
   */
  #onStateChanges(nextState) {
    if (nextState && !isEqual(this.#state, nextState)) {
      const shouldRenderDuration = this.#state?.duration !== nextState.duration;
      const shouldRenderPrice = this.#state?.price !== nextState.price;

      this.debouncedMilageAnalytics();

      //Store state
      this.#state = nextState;

      if (shouldRenderPrice) this.elements.price.dataset.value = this.#state.price;
      if (shouldRenderDuration) {
        this.elements.duration.value = this.#state.duration;
        this.#renderDistance();
      }
    }
  }

  /**
   * CarPageApp state observer.
   * @public
   * @param {object} fullStateObject comes from carPageApp object
   */
  handleStateChanges(fullStateObject) {
    const filteredStateObject = this.#filterState(fullStateObject);
    filteredStateObject && this.#onStateChanges(filteredStateObject);
  }

  /**
   * Send milage analytics data
   */
  mileageAnalytics() {
    analytics.pushUIInteractionAnalytics({
      element: 'input',
      elementName: 'Vaihda sopimuksen kestoa',
      type: 'click',
    });
  }

  /**
   * Static function that is callable from master.js. Instantiate FiksuGreenDiiliForm.
   * @public
   * @param {object} carPageApp car page state object
   */
  static init(carPageApp) {
    document.querySelectorAll('.fiksugreendiili-form').forEach(element => {
      new FiksuGreenDiiliForm(element, carPageApp);
    });
  }
}

export default FiksuGreenDiiliForm;
